import { motion } from "framer-motion";
import { Quote, ArrowRight, Star } from "lucide-react";
import { T } from "../data/content";
import { useLang } from "../context/LangContext";
import { useSiteData } from "../context/SiteDataContext";
import { Stars } from "./Stars";

export const ReviewsSection = () => {
  const { tr } = useLang();
  const { reviews, brand } = useSiteData();
  const items = reviews.slice(0, 3);
  const avg = reviews.length ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length) : 0;

  return (
    <section id="reviews" className="relative bg-[#0C0806] py-24 lg:py-36" data-testid="reviews-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="mb-5 flex items-center gap-3">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.reviews.label)}</span>
            </div>
            <h2 className="font-serif text-5xl tracking-tight text-[#F5ECDA] md:text-6xl">{tr(T.reviews.title)}</h2>
            {reviews.length > 0 && (
              <div className="mt-4 flex items-center gap-3" data-testid="avg-rating">
                <Stars value={Math.round(avg)} size={18} />
                <span className="font-serif text-2xl text-[#D4AF37]">{avg.toFixed(1)}</span>
                <span className="text-sm text-[#B7A78C]">· {reviews.length} {tr(T.reviews.count)}</span>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={brand.googleReview} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 self-start rounded-full border border-white/20 px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-[#F5ECDA] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="reviews-google">
              <Star className="h-4 w-4 fill-[#D4AF37] text-[#D4AF37]" /> {tr(T.reviews.google)}
            </a>
            <a href="/avis" className="inline-flex items-center gap-2 self-start rounded-full border border-white/20 px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-[#F5ECDA] transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37]" data-testid="reviews-see-all">
              {tr(T.reviews.seeAll)} <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        {items.length === 0 ? (
          <p className="py-10 text-[#B7A78C]">{tr(T.reviews.empty)}</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {items.map((r, i) => (
              <motion.figure
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col rounded-lg border border-[#2E2418] bg-[#15100B] p-7"
                data-testid={`review-card-${r.id}`}
              >
                <Quote className="mb-4 h-7 w-7 text-[#D4AF37]/60" />
                <blockquote className="flex-1 text-base leading-relaxed text-[#E9DFCB]">"{r.message}"</blockquote>
                {r.image && <img src={r.image} alt="" className="mt-4 h-40 w-full rounded-md object-cover" />}
                {r.reply && (
                  <div className="mt-4 rounded-md border-l-2 border-[#D4AF37] bg-[#0C0806] p-3">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">{tr(T.reviews.replyLabel)}</p>
                    <p className="mt-1 text-sm italic text-[#B7A78C]">{r.reply}</p>
                  </div>
                )}
                <figcaption className="mt-5 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="font-serif text-lg text-[#F5ECDA]">{r.name}</span>
                  <Stars value={r.rating} />
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <a href="/avis" className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-7 py-3.5 text-xs font-medium uppercase tracking-[0.15em] text-black transition-colors hover:bg-[#F3E5AB]" data-testid="reviews-leave-cta">
            {tr(T.reviews.leave)}
          </a>
        </div>
      </div>
    </section>
  );
};
